import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../axiosConfig';

// Async thunks
export const fetchCustomerGroups = createAsyncThunk(
  'customerGroups/fetchCustomerGroups',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/customer-groups');
      return response.data; // API returns data directly, not wrapped in data property
    } catch (error) {
      console.error('Error fetching customer groups:', error);
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch customer groups');
    }
  }
);

export const createCustomerGroup = createAsyncThunk(
  'customerGroups/createCustomerGroup',
  async (groupData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/customer-groups', groupData);
      return response.data; // API returns data directly
    } catch (error) {
      console.error('Error creating customer group:', error);
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to create customer group');
    }
  }
);

export const updateCustomerGroup = createAsyncThunk(
  'customerGroups/updateCustomerGroup',
  async ({ groupId, groupData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/customer-groups/${groupId}`, groupData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update customer group');
    }
  }
);

export const deleteCustomerGroup = createAsyncThunk(
  'customerGroups/deleteCustomerGroup',
  async (groupId, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/customer-groups/${groupId}`);
      return groupId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete customer group');
    }
  }
);

export const addCustomerToGroup = createAsyncThunk(
  'customerGroups/addCustomerToGroup',
  async ({ groupId, customerId }, { rejectWithValue }) => {
    try {
      await axiosInstance.post(`/customer-groups/${groupId}/add-customers`, { customerIds: [customerId] });
      return { groupId, customerId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to add customer to group');
    }
  }
);

export const removeCustomerFromGroup = createAsyncThunk(
  'customerGroups/removeCustomerFromGroup',
  async ({ groupId, customerId }, { rejectWithValue }) => {
    try {
      await axiosInstance.post(`/customer-groups/${groupId}/remove-customers`, { customerIds: [customerId] });
      return { groupId, customerId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to remove customer from group');
    }
  }
);

export const fetchGroupCustomers = createAsyncThunk(
  'customerGroups/fetchGroupCustomers',
  async ({ groupId, page = 1, limit = 50 }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/customer-groups/${groupId}/customers?page=${page}&limit=${limit}`);
      return { groupId, ...response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch group customers');
    }
  }
);

export const addTagsToCustomer = createAsyncThunk(
  'customerGroups/addTagsToCustomer',
  async ({ customerId, tags }, { rejectWithValue }) => {
    try {
      await axiosInstance.post('/customer-groups/tags/add', { customerIds: [customerId], tags });
      return { customerId, tags };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to add tags to customer');
    }
  }
);

export const removeTagFromCustomer = createAsyncThunk(
  'customerGroups/removeTagFromCustomer',
  async ({ customerId, tag }, { rejectWithValue }) => {
    try {
      await axiosInstance.post('/customer-groups/tags/remove', { customerIds: [customerId], tags: [tag] });
      return { customerId, tag };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to remove tag from customer');
    }
  }
);

export const fetchAllTags = createAsyncThunk(
  'customerGroups/fetchAllTags',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/customer-groups/tags/all');
      return response.data;
    } catch (error) {
      console.error('Error fetching tags:', error);
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch tags');
    }
  }
);

const initialState = {
  groups: [],
  currentGroup: null,
  groupCustomers: {},
  allTags: [],
  status: 'idle',
  error: null,
  loading: false
};

const customerGroupsSlice = createSlice({
  name: 'customerGroups',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentGroup: (state, action) => {
      state.currentGroup = action.payload;
    },
    clearCurrentGroup: (state) => {
      state.currentGroup = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch customer groups
      .addCase(fetchCustomerGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload;
      })
      .addCase(fetchCustomerGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create customer group
      .addCase(createCustomerGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCustomerGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = [action.payload, ...state.groups];
      })
      .addCase(createCustomerGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update customer group
      .addCase(updateCustomerGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCustomerGroup.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.groups.findIndex(group => group._id === action.payload._id);
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
      })
      .addCase(updateCustomerGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete customer group
      .addCase(deleteCustomerGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCustomerGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = state.groups.filter(group => group._id !== action.payload);
        if (state.currentGroup?._id === action.payload) {
          state.currentGroup = null;
        }
      })
      .addCase(deleteCustomerGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch group customers
      .addCase(fetchGroupCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.groupCustomers[action.payload.groupId] = action.payload;
      })
      .addCase(fetchGroupCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch all tags
      .addCase(fetchAllTags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllTags.fulfilled, (state, action) => {
        state.loading = false;
        state.allTags = action.payload;
      })
      .addCase(fetchAllTags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, setCurrentGroup, clearCurrentGroup } = customerGroupsSlice.actions;
export default customerGroupsSlice.reducer;
